import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { EditPaymentModal, EditPaymentFormData } from "@/components/payments/EditPaymentModal";
import {
  paymentService,
  Payment,
  EPaymentMethod,
  EPaymentStatus,
  
} from "@/services/payment";
import { supplierService } from "@/services/supplier";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  Eye,
  Pencil,
  Truck,
  ExternalLink,
} from "lucide-react";
import { EownerType } from "@/services/subscription";

const PAGE_SIZE = 10;

const paymentMethodLabels: Record<EPaymentMethod, string> = {
  [EPaymentMethod.TRANSFER]: "Transferencia",
  [EPaymentMethod.CASH]: "Efectivo",
  [EPaymentMethod.NEQUI]: "Nequi",
  [EPaymentMethod.DAVIPLATA]: "Daviplata",
  [EPaymentMethod.BANCOLOMBIA]: "Bancolombia",
  [EPaymentMethod.PAYPAL]: "PayPal",
  [EPaymentMethod.BINANCE]: "Binance",
  [EPaymentMethod.YAPE]: "Yape",
};

const paymentStatusLabels: Record<EPaymentStatus, string> = {
  [EPaymentStatus.COMPLETED]: "Completado",
  [EPaymentStatus.PENDING]: "Pendiente",
  [EPaymentStatus.CANCELLED]: "Cancelado",
  [EPaymentStatus.REFUNDED]: "Reembolsado",
};

const paymentStatusStyles: Record<EPaymentStatus, string> = {
  [EPaymentStatus.COMPLETED]:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  [EPaymentStatus.PENDING]:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  [EPaymentStatus.CANCELLED]:
    "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  [EPaymentStatus.REFUNDED]:
    "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
};

function generatePaginationNumbers(
  currentPage: number,
  totalPages: number,
): (number | string)[] {
  const delta = 1;
  const range: (number | string)[] = [];
  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= currentPage - delta && i <= currentPage + delta)
    ) {
      range.push(i);
    } else if (range[range.length - 1] !== "...") {
      range.push("...");
    }
  }
  return range;
}

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

export default function SupplierPayments() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [supplierFilter, setSupplierFilter] = useState<string>("ALL");
  const [methodFilter, setMethodFilter] = useState<string>("ALL");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [supplierFilter, methodFilter, statusFilter]);

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: EditPaymentFormData }) =>
      paymentService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supplier-payments"] });
      setIsEditModalOpen(false);
      setEditingPayment(null);
      toast.success("Pago actualizado exitosamente");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const { data: suppliersResponse } = useQuery({
    queryKey: ["suppliers", "all"],
    queryFn: () => supplierService.getAll({ limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });
  const suppliers = suppliersResponse?.data || [];

  const {
    data: paymentsResponse,
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: [
      "supplier-payments",
      currentPage,
      supplierFilter,
      methodFilter,
      statusFilter,
    ],
    queryFn: () =>
      paymentService.getAll({
        page: currentPage,
        limit: PAGE_SIZE,
        ownerType: EownerType.SUPPLIER,
        supplierId:
          supplierFilter !== "ALL" ? Number(supplierFilter) : undefined,
        paymentMethod:
          methodFilter !== "ALL" ? (methodFilter as EPaymentMethod) : undefined,
        status:
          statusFilter !== "ALL" ? (statusFilter as EPaymentStatus) : undefined,
      }),
  });

  const payments = paymentsResponse?.data || [];
  const pagination = paymentsResponse?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const totalItems = pagination?.totalItems || 0;

  return (
    <DashboardLayout
      title="Pagos a Proveedores"
      subtitle="Pagos asociados a proveedores de cuentas"
    >
      <div className="space-y-6 animate-fade-in">
        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-[200px]">
            <label className="text-sm font-medium text-muted-foreground mb-1 block">
              Proveedor
            </label>
            <Select value={supplierFilter} onValueChange={setSupplierFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos los proveedores</SelectItem>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full sm:w-[180px]">
            <label className="text-sm font-medium text-muted-foreground mb-1 block">
              Método de pago
            </label>
            <Select value={methodFilter} onValueChange={setMethodFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos los métodos</SelectItem>
                {Object.values(EPaymentMethod).map((method) => (
                  <SelectItem key={method} value={method}>
                    {paymentMethodLabels[method]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full sm:w-[180px]">
            <label className="text-sm font-medium text-muted-foreground mb-1 block">
              Estado
            </label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos los estados</SelectItem>
                {Object.values(EPaymentStatus).map((status) => (
                  <SelectItem key={status} value={status}>
                    {paymentStatusLabels[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Error */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>
              Error al cargar los pagos.
            </AlertDescription>
          </Alert>
        )}

        {/* Table */}
        <div className="card-elevated overflow-hidden relative">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}

          {isLoading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground mt-2">Cargando pagos...</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold">Proveedor</TableHead>
                  <TableHead className="font-semibold text-right">
                    Monto
                  </TableHead>
                  <TableHead className="font-semibold">Fecha de pago</TableHead>
                  <TableHead className="font-semibold">Método</TableHead>
                  <TableHead className="font-semibold">Estado</TableHead>
                  <TableHead className="font-semibold">Notas</TableHead>
                  <TableHead className="font-semibold text-right">
                    Acciones
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow
                    key={payment.id}
                    className="transition-colors hover:bg-muted/30"
                  >
                    {/* Proveedor */}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <Truck className="h-4 w-4 text-primary" />
                        </div>
                        <button
                          type="button"
                          className="flex items-center gap-1 font-medium text-primary hover:underline focus:outline-none group"
                          onClick={() =>
                            payment.supplier &&
                            navigate(`/suppliers/${payment.supplier.id}`)
                          }
                        >
                          <span>
                            {payment.supplier?.name ||
                              (payment.supplierId
                                ? `Proveedor #${payment.supplierId}`
                                : "—")}
                          </span>
                          {payment.supplier && (
                            <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity" />
                          )}
                        </button>
                      </div>
                    </TableCell>

                    <TableCell className="text-right font-semibold">
                      ${Number(payment.amount).toFixed(0)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(payment.paymentDate)}
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        {paymentMethodLabels[payment.paymentMethod]}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${paymentStatusStyles[payment.status]}`}
                      >
                        {paymentStatusLabels[payment.status]}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">
                      {payment.notes || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {payment.subscriptionId && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              navigate(
                                `/subscriptions/${payment.subscriptionId}`,
                              )
                            }
                          >
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            setEditingPayment(payment);
                            setIsEditModalOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {!isLoading && payments.length === 0 && (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">
                No se encontraron pagos de proveedores
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Los pagos de proveedores se generan con sus suscripciones
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Mostrando {payments.length} de {totalItems} pagos
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1 || isFetching}
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              <div className="flex items-center gap-1">
                {generatePaginationNumbers(currentPage, totalPages).map(
                  (page, idx) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-2 text-muted-foreground"
                      >
                        ...
                      </span>
                    ) : (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        className="w-9"
                        onClick={() => setCurrentPage(page as number)}
                        disabled={isFetching}
                      >
                        {page}
                      </Button>
                    ),
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages || isFetching}
              >
                Siguiente
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <EditPaymentModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        payment={editingPayment}
        onSave={(data) => {
          if (editingPayment) {
            updateMutation.mutate({ id: editingPayment.id, data });
          }
        }}
        isPending={updateMutation.isPending}
      />
    </DashboardLayout>
  );
}
